import {get, isEmpty, isNil} from "lodash"
import theme from "theme/index"
import themeVars from "theme/themeVars"
import {iconNames} from "components/icon"
import {getSearchTerm} from "helpers/string"
import {ApprovalPersonTypes} from "helpers/quotes"
import {userHasPermission} from "helpers/user"
import {permissions} from "helpers/constants"

export const ApprovalStatus = {
  draft: 'DRAFT',
  approved: 'APPROVED',
  inReview: 'IN_REVIEW',
  changesRequested: 'CHANGE_REQ',
  rejected: 'REJECTED',
}

export const ApprovalHistoryType = {
  created: 'CREATED',
  newRevisionCreated: 'NEW_REVISION_CREATED',
  ownerIdChanged: 'OWNER_ID_CHANGED',
  descriptionChanged: 'DESCRIPTION_CHANGED',
  statusChanged: 'STATUS_CHANGED',
  notesChanged: 'NOTES_CHANGED',
  expiryDateChanged: 'EXPIRY_DATE_CHANGED',
  approvedDateChanged: 'APPROVED_DATE_CHANGED',
  requestedChangesDateChanged: 'REQUESTED_CHANGES_DATE_CHANGED',
  rejectedDateChanged: 'REJECTED_DATE_CHANGED',
  rejected: 'REJECTED',
  approved: 'APPROVED',
  changesRequested: 'REQUESTED_CHANGES',
  emailSent: 'EMAIL_SENT',
  bulkUpdate: 'BULK_UPDATE',
  commentAdded: 'COMMENT_ADDED',
  statusReverted: 'STATUS_REVERTED',
}

const CustomerApprovalHistoryTypes = [
  ApprovalHistoryType.created,
  ApprovalHistoryType.newRevisionCreated,
  ApprovalHistoryType.rejected,
  ApprovalHistoryType.rejectedDateChanged,
  ApprovalHistoryType.requestedChangesDateChanged,
  ApprovalHistoryType.approvedDateChanged,
  ApprovalHistoryType.commentAdded,
  ApprovalHistoryType.approved,
  ApprovalHistoryType.changesRequested,
  ApprovalHistoryType.statusReverted,
]

const VALID_APPROVAL_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
]

export const approvalDrawerFields = {
  default: true,
  approvalhistory: true,
}

export const getApprovalStatusName = (status) => {
  switch (status) {
    case ApprovalStatus.draft:
      return 'Draft'
    case ApprovalStatus.approved:
      return 'Approved'
    case ApprovalStatus.inReview:
      return 'In Review'
    case ApprovalStatus.changesRequested:
      return 'Changes Requested'
    case ApprovalStatus.rejected:
      return 'Rejected'
    default:
      return null
  }
}

export const getApprovalStatusChangeTitle = (status) => {
  switch (status) {
    case ApprovalStatus.approved:
      return 'Approve'
    case ApprovalStatus.changesRequested:
      return 'Request Changes'
    case ApprovalStatus.rejected:
      return 'Reject'
    default:
      return null
  }
}

export const getApprovalStatusChangeClassName = (status) => {
  switch (status) {
    case ApprovalStatus.approved:
      return 'approve'
    case ApprovalStatus.changesRequested:
      return 'requestChanges'
    case ApprovalStatus.rejected:
      return 'reject'
    default:
      return null
  }
}

export const getColorForApprovalStatus = (muiTheme, status) => {
  if (isNil(status)) return ''
  switch (status) {
    case ApprovalStatus.draft:
      return muiTheme.palette.approvalStatus.draft
    case ApprovalStatus.approved:
      return muiTheme.palette.approvalStatus.approved
    case ApprovalStatus.inReview:
      return muiTheme.palette.approvalStatus.inReview
    case ApprovalStatus.changesRequested:
      return muiTheme.palette.approvalStatus.changesRequested
    case ApprovalStatus.rejected:
      return muiTheme.palette.approvalStatus.rejected
    default:
      return ''
  }
}

export const getApprovalStatusIconColor = (theme, status) => {
  if (isNil(status)) return ''
  switch (status) {
    case ApprovalStatus.approved:
      return theme.palette.approvalStatus.approved
    case ApprovalStatus.changesRequested:
      return theme.palette.approvalStatus.changesRequested
    case ApprovalStatus.rejected:
      return theme.palette.approvalStatus.rejected
    default:
      return theme.palette.approvalStatus.draft
  }
}

export const getApprovalStatusIcon = (status) => {
  if (isNil(status)) return ''
  switch (status) {
    case ApprovalStatus.approved:
      return iconNames.approvalApprove
    case ApprovalStatus.changesRequested:
      return iconNames.approvalRequestChanges
    case ApprovalStatus.rejected:
      return iconNames.approvalReject
    default:
      return ''
  }
}

export const getApprovalStatusFilterOptions = () => [
  getApprovalDraftStatusOption(),
  getApprovalInReviewStatusOption(),
  getApprovalApprovedStatusOption(),
  getApprovalChangesRequestedStatusOption(),
  getApprovalRejectedStatusOption(),
]

export const getApprovalStatusOptions = (currentStatus, isReadOnly) => {
  if (isReadOnly) {
    return [
      getApprovalDraftStatusOption(),
      getApprovalInReviewStatusOption(),
      getApprovalChangesRequestedStatusOption(),
      getApprovalApprovedStatusOption(),
      getApprovalRejectedStatusOption(),
    ]
  }

  switch (currentStatus) {
    case ApprovalStatus.draft:
      return [
        getApprovalDraftStatusOption(),
        getApprovalInReviewStatusOption(),
        getApprovalRejectedStatusOption(),
      ]
    case ApprovalStatus.inReview:
      return [
        getApprovalInReviewStatusOption(),
        getApprovalDraftStatusOption(),
        getApprovalRejectedStatusOption(),
      ]
    case ApprovalStatus.approved:
      return [getApprovalApprovedStatusOption()]
    case ApprovalStatus.changesRequested:
      return [getApprovalChangesRequestedStatusOption()]
    case ApprovalStatus.rejected:
      return [getApprovalRejectedStatusOption()]
    default:
      return [getApprovalDraftStatusOption()]
  }
}

export const getApprovalDraftStatusOption = () => {
  return {
    key: ApprovalStatus.draft,
    colour: getColorForApprovalStatus(theme, ApprovalStatus.draft),
    label: getApprovalStatusName(ApprovalStatus.draft),
    value: ApprovalStatus.draft,
  }
}

export const getApprovalApprovedStatusOption = () => {
  return {
    key: ApprovalStatus.approved,
    colour: getColorForApprovalStatus(theme, ApprovalStatus.approved),
    label: getApprovalStatusName(ApprovalStatus.approved),
    value: ApprovalStatus.approved,
  }
}

export const getApprovalInReviewStatusOption = () => {
  return {
    key: ApprovalStatus.inReview,
    colour: getColorForApprovalStatus(theme, ApprovalStatus.inReview),
    label: getApprovalStatusName(ApprovalStatus.inReview),
    value: ApprovalStatus.inReview,
  }
}

export const getApprovalChangesRequestedStatusOption = () => {
  return {
    key: ApprovalStatus.changesRequested,
    colour: getColorForApprovalStatus(theme, ApprovalStatus.changesRequested),
    label: getApprovalStatusName(ApprovalStatus.changesRequested),
    value: ApprovalStatus.changesRequested,
  }
}

export const getApprovalRejectedStatusOption = () => {
  return {
    key: ApprovalStatus.rejected,
    colour: getColorForApprovalStatus(theme, ApprovalStatus.rejected),
    label: getApprovalStatusName(ApprovalStatus.rejected),
    value: ApprovalStatus.rejected,
  }
}

export const getApprovalStatusSelectorValue = (isReadOnly, isNew, status) => {
  if (isReadOnly) return status
  return isNew ? ApprovalStatus.draft : status
}

export const isApprovalStatusSelectorDisabled = (isReadOnly, isNew, status) => {
  return isReadOnly || isNew || getApprovalStatusOptions(status).length === 1
}

export const canSaveApproval = (approval) => {
  const desc = get(approval, 'description', null)
  const status = get(approval, 'status', null)
  return !isEmpty(desc) && !isEmpty(status)
}

export const isLastHistoryItem = (history, index) => {
  return index === history.length - 1
}

export const getHistoryItemClassNames = (isLastItemInList, historyType) => {
  return [...isLastItemInList ? ['item', 'item-last'] : ['item'], historyType.toLowerCase()]
}

export const getHistoryIconName = (historyType) => {
  switch (historyType) {
    case ApprovalHistoryType.created:
      return iconNames.approvalHistoryCreated
    case ApprovalHistoryType.newRevisionCreated:
      return iconNames.approvalHistoryCreated
    case ApprovalHistoryType.ownerIdChanged:
    case ApprovalHistoryType.descriptionChanged:
    case ApprovalHistoryType.statusChanged:
    case ApprovalHistoryType.statusReverted:
    case ApprovalHistoryType.notesChanged:
    case ApprovalHistoryType.expiryDateChanged:
    case ApprovalHistoryType.approvedDateChanged:
    case ApprovalHistoryType.requestedChangesDateChanged:
    case ApprovalHistoryType.rejectedDateChanged:
    case ApprovalHistoryType.bulkUpdate:
      return iconNames.approvalHistoryEdit
    case ApprovalHistoryType.rejected:
      return iconNames.approvalReject
    case ApprovalHistoryType.changesRequested:
      return iconNames.approvalRequestChanges
    case ApprovalHistoryType.approved:
      return iconNames.approvalApprove
    case ApprovalHistoryType.emailSent:
      return iconNames.emailed
    case ApprovalHistoryType.commentAdded:
      return iconNames.commentAdded
    default:
      return iconNames.warning
  }
}

export const getHistoryIconBackgroundColor = (historyType) => {
  switch (historyType) {
    case ApprovalHistoryType.approved:
      return theme.palette.approvalStatus.approved
    case ApprovalHistoryType.changesRequested:
      return theme.palette.approvalStatus.changesRequested
    case ApprovalHistoryType.rejected:
      return theme.palette.approvalStatus.rejected
    case ApprovalHistoryType.created:
      return theme.palette.lightIconColor
    case ApprovalHistoryType.newRevisionCreated:
      return theme.palette.lightIconColor
    default:
      return theme.palette.primaryIconColor
  }
}

export const isRejectedHistoryType = (historyType) => {
  return historyType === ApprovalHistoryType.rejected
}

export const isApprovalDraft = (approval) => {
  return get(approval, 'status', null) === ApprovalStatus.draft
}

export const isApprovalInReview = (approval) => {
  return get(approval, 'status', null) === ApprovalStatus.inReview
}

export const isApprovalApproved = (approval) => {
  return get(approval, 'status', null) === ApprovalStatus.approved
}

export const isApprovalChangesRequested = (approval) => {
  return get(approval, 'status', null) === ApprovalStatus.changesRequested
}

export const isApprovalRejected = (approval) => {
  return get(approval, 'status', null) === ApprovalStatus.rejected
}

export const shouldApprovalCurrentStatusBeVisible = (approval) => {
  return isApprovalApproved(approval) || isApprovalChangesRequested(approval) || isApprovalRejected(approval)
}

export const shouldApprovalActionsBeVisible = (approval) => {
  return isApprovalDraft(approval) || isApprovalInReview(approval)
}

export const isApprovalFileTypeValid = (file) => {
  const fileType = get(file, 'type', '')
  return !isEmpty(fileType) ? VALID_APPROVAL_FILE_TYPES.includes(fileType) : false
}

export const getNewHighlightedApprovalIndex = (indexToRemove, currentHighlightedIndex) => {
  return indexToRemove <= currentHighlightedIndex
    ? Math.max(0, currentHighlightedIndex - 1)
    : currentHighlightedIndex
}

export const getFileUploaderStyles = (hideBackground) => {
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      width: '100%',
      height: '100%',
      backgroundColor: hideBackground ? themeVars.palette.blackColor : themeVars.palette.background2Color,
    },
  }
}

export const getUpdatedFilesForCache = (
  cachedFiles,
  isServerFile,
  approvalDocuments,
  deletedFiles,
  indexToRemove
) => {
  let updatedFiles = cachedFiles
  if (!isServerFile) {
    const filteredServerDocs = approvalDocuments.filter(doc => !deletedFiles.includes(doc.id))
    const cachedFileIndex = indexToRemove - filteredServerDocs.length
    updatedFiles = cachedFiles.filter((_, i) => i !== cachedFileIndex)
  }
  return updatedFiles
}

export const getFileKey = (file) => {
  const fileName = get(file, 'name', '')
  const fileSizeBytes = get(file, 'sizeBytes', null)
  const fileSize = !isNil(fileSizeBytes) ? fileSizeBytes : get(file, 'size', 0)

  return `${fileName}-${fileSize}`
}

export const getFileKeys = (files) => {
  return files.map(file => getFileKey(file))
}

export const isFileAlreadySelected = (localFiles, serverFiles, selectedFile) => {
  const existingFileKeys = [...getFileKeys(localFiles), ...getFileKeys(serverFiles)]
  return existingFileKeys.includes(getFileKey(selectedFile))
}

export const isMobileView = (window) => {
  return window.innerWidth < 768
}

export const isTabletView = (window) => {
  return window.innerWidth >= 768 && window.innerWidth < 1280
}

export const sortApprovalsByCreatedDate = (approvals) => {
  return approvals.sort((a, b) => {
    return new Date(b.createdDate) - new Date(a.createdDate)
  })
}

export const getInReviewApprovals = (approvals) => {
  if (isEmpty(approvals)) return []
  return approvals.filter(approval => approval.status === ApprovalStatus.inReview)
}

export const getApprovalThumbnailClassName = (thumbnailIndex, highlightedIndex) => {
  return thumbnailIndex === highlightedIndex
    ? ['approvalThumbnail-container', 'approvalThumbnail-container-highlighted']
    : 'approvalThumbnail-container'
}

export const getFilteredApprovalHistoryForCustomer = (history) => {
  return history.filter(historyItem => {
    return CustomerApprovalHistoryTypes.includes(historyItem.type)
  })
}

export const downloadFile = (file) => {
  const downloadUrl = get(file, 'downloadUrl', null)
  if (isNil(downloadUrl)) return

  const fileName = get(file, 'name', 'download')

  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const isApprovalStatusChangeSubmitEnabled = (status, name, comment) => {
  return status === ApprovalStatus.approved
    ? !isEmpty(name.trim())
    : !isEmpty(name.trim()) && !isEmpty(comment.trim())
}

export const isApprovalChangeStatusCommentFieldMandatory = (status) => {
  return status !== ApprovalStatus.approved
}

export const filterApprovalsThatCanRevertStatusById = (approvals, id) => {
  return approvals.filter(item => item !== id)
}

export const getApprovalDrawerFileThumbnailUrl = (file) => {
  return get(file, 'isServerFile', false)
    ? get(file, 'thumbnailUrl', '')
    : URL.createObjectURL(file)
}

export const canCreateNewRevision = (status) => {
  return status === ApprovalStatus.rejected || status === ApprovalStatus.changesRequested
}

export const getLatestApprovalRevision = (approvals) => {
  if (isEmpty(approvals)) return null

  return approvals.reduce((latest, current) => {
    return current.createdDate > latest.createdDate ? current : latest
  })
}

export const getUpdatedApprovalRevisions = (updatedRevision, existingRevisions) => {
  return existingRevisions.map(existingRevision =>
    existingRevision.id === updatedRevision.id ? updatedRevision : existingRevision
  )
}

export const getApprovalFilterParams = (
  search,
  statusTypes,
  companies,
  userTypes,
  contacts,
  dateTypes,
  targetStart,
  targetEnd,
  jobTypes
) => {
  const params = {}
  if (getSearchTerm(search))
    params['search'] = search

  if (!isEmpty(statusTypes)) {
    params['status'] = statusTypes
  }

  if (companies)
    params['companyId'] = companies.map(company => company.value)

  if (!isEmpty(contacts)) {
    params['personId'] = contacts
    const allUserTypes = ApprovalPersonTypes.map(type => type.value)

    if (!isEmpty(userTypes)) {
      userTypes.forEach(type => {
        params[type] = true
      })
      const nonSelectedUserTypes = allUserTypes.filter(filter => !userTypes.includes(filter))
      nonSelectedUserTypes.forEach(type => {
        params[type] = false
      })
    } else {
      allUserTypes.forEach(type => {
        params[type] = true
      })
    }
  }

  if (targetStart && targetEnd) {
    dateTypes.forEach(dateType => {
      params[`${dateType}Start`] = targetStart
      params[`${dateType}End`] = targetEnd
    })
  }

  if (!isEmpty(jobTypes)) {
    params['jobTypes'] = jobTypes
  }

  return params
}

export const isValidHighlightedFile = (selectedRevisionDocuments, highlightedFileIndex) => {
  return !isEmpty(selectedRevisionDocuments) &&
    highlightedFileIndex >= 0 &&
    highlightedFileIndex < selectedRevisionDocuments.length
}


export const canManageApprovals = (user) =>
  userHasPermission(user, permissions.MANAGE_APPROVALS)

export const canManageApprovalSettings = (user) =>
  userHasPermission(user, permissions.MANAGE_APPROVALS_SETTINGS)

export const isLoadApprovalWhenHashIdIsPresent = (baseApprovalId, order, approvalDetails) => {
  const approvalId = get(approvalDetails, 'baseApprovalId', null)

  const hashId = String(baseApprovalId)
  const currentId = approvalId !== null ? String(approvalId) : null

  return !isEmpty(baseApprovalId) &&
    !isEmpty(order) &&
    (!approvalDetails || currentId !== hashId)
}
